import React, { useState } from "react";
import {
  useApiProviders,
  useDeleteApiProvider,
  useUpdateApiProvider,
  useProviderCarriers,
  type ApiProvider,
} from "@/api/api-providers";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  Edit,
  Trash2,
  Power,
  ChevronDown,
  ChevronUp,
  Globe,
  Code2,
  Loader2,
} from "lucide-react";
import { ApiProviderDialog } from "./api-provider-dialog";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";

// Component to show potential carriers from API provider
const PotentialCarriers = ({ provider }: { provider: ApiProvider }) => {
  // List of providers that support listing carriers
  const SUPPORTS_LISTING = ["shipstation"];

  const supportsListing = SUPPORTS_LISTING.includes(provider.code);

  const { data: providerCarriers, isLoading } = useProviderCarriers(
    provider.id,
    supportsListing // Only fetch for providers that support listing
  );

  // Don't show section at all for providers that don't support listing
  if (!supportsListing) {
    return null;
  }

  // Filter out carriers that are already in supportedCarrierCodes
  const potentialCarriers = providerCarriers?.filter(
    (carrier) =>
      !provider.supportedCarrierCodes?.includes(
        carrier.carrierCode.toLowerCase()
      )
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading potential carriers...
      </div>
    );
  }

  if (!potentialCarriers || potentialCarriers.length === 0) {
    return (
      <span className="text-sm text-muted-foreground">
        All available carriers are already configured
      </span>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {potentialCarriers.map((carrier) => (
        <Badge
          key={carrier.carrierId}
          variant="outline"
          className="bg-blue-50 border-blue-200"
        >
          {carrier.carrierCode}
          {carrier.nickname && (
            <span className="ml-1 text-xs text-muted-foreground">
              ({carrier.nickname})
            </span>
          )}
        </Badge>
      ))}
    </div>
  );
};

export const ApiProvidersTable = () => {
  const { data: providers, isLoading } = useApiProviders();
  const deleteProviderMutation = useDeleteApiProvider();
  const updateProviderMutation = useUpdateApiProvider();

  const [selectedProvider, setSelectedProvider] = useState<ApiProvider | null>(
    null
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<ApiProvider | null>(
    null
  );
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const handleEdit = (provider: ApiProvider) => {
    setSelectedProvider(provider);
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!providerToDelete) return;

    try {
      await deleteProviderMutation.mutateAsync(providerToDelete.id);
      toast.success("API provider deleted successfully");
    } catch {
      toast.error("Failed to delete API provider");
    }
    setDeleteConfirmOpen(false);
    setProviderToDelete(null);
  };

  const handleToggle = async (provider: ApiProvider) => {
    try {
      await updateProviderMutation.mutateAsync({
        id: provider.id,
        data: { active: !provider.active },
      });
      toast.success(
        `API provider ${provider.active ? "deactivated" : "activated"} successfully`
      );
    } catch {
      toast.error("Failed to update API provider status");
    }
  };

  const toggleRowExpansion = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>API Providers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-2xl font-bold">API Providers</CardTitle>
          <Button
            onClick={() => {
              setSelectedProvider(null);
              setIsDialogOpen(true);
            }}
            size="sm"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Provider
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Base URL</TableHead>
                <TableHead>Supported Carriers</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {providers?.map((provider) => (
                <React.Fragment key={provider.id}>
                  <TableRow className="group hover:bg-muted/50">
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleRowExpansion(provider.id)}
                      >
                        {expandedRows.has(provider.id) ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      <div className="flex items-center gap-2">
                        <Code2 className="h-4 w-4 text-muted-foreground" />
                        {provider.code}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {provider.name}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                          {provider.baseUrl}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {provider.supportedCarrierCodes
                          ?.slice(0, 3)
                          .map((code) => (
                            <Badge
                              key={code}
                              variant="secondary"
                              className="text-xs"
                            >
                              {code}
                            </Badge>
                          ))}
                        {provider.supportedCarrierCodes?.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{provider.supportedCarrierCodes.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={provider.active ? "default" : "secondary"}
                        className="flex items-center gap-1 w-fit"
                      >
                        <div
                          className={`w-2 h-2 rounded-full ${provider.active ? "bg-green-500" : "bg-gray-500"}`}
                        />
                        {provider.active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggle(provider)}
                          disabled={updateProviderMutation.isPending}
                        >
                          <Power
                            className={`h-4 w-4 ${provider.active ? "text-red-500" : "text-green-500"}`}
                          />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Edit ${provider.name}`}
                          onClick={() => handleEdit(provider)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setProviderToDelete(provider);
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {expandedRows.has(provider.id) && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted/30">
                        <Collapsible open={expandedRows.has(provider.id)}>
                          <CollapsibleContent>
                            <div className="p-4 space-y-4">
                              <div>
                                <h4 className="text-sm font-semibold mb-2">
                                  All Supported Carriers
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {provider.supportedCarrierCodes?.map(
                                    (code) => (
                                      <Badge key={code} variant="outline">
                                        {code}
                                      </Badge>
                                    )
                                  )}
                                  {(!provider.supportedCarrierCodes ||
                                    provider.supportedCarrierCodes.length ===
                                      0) && (
                                    <span className="text-sm text-muted-foreground">
                                      No carriers configured
                                    </span>
                                  )}
                                </div>
                              </div>
                              {provider.code === "shipstation" && (
                                <div>
                                  <h4 className="text-sm font-semibold mb-2">
                                    Potential Carriers
                                  </h4>
                                  <PotentialCarriers provider={provider} />
                                </div>
                              )}
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-muted-foreground">
                                    Created:
                                  </span>{" "}
                                  {new Date(
                                    provider.createdAt
                                  ).toLocaleDateString()}
                                </div>
                                <div>
                                  <span className="text-muted-foreground">
                                    Updated:
                                  </span>{" "}
                                  {new Date(
                                    provider.updatedAt
                                  ).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
              {(!providers || providers.length === 0) && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No API providers configured. Click "Add Provider" to get
                    started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ApiProviderDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        provider={selectedProvider}
      />

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete API Provider</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{providerToDelete?.name}"? This
              action cannot be undone and may affect active integrations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
